// ============================================================================
// Canonical Fleet single-entry route contracts
// ============================================================================
//
// EquipmentHome owns a broad `/fleet/*` legacy space that now carries FIVE
// distinct release claims: the index (`/fleet`, `/fleet/fuel-log`), one
// unreserved record segment (`/fleet/<slug>`), and the two single-entry record
// namespaces defined here. This module holds the two entry predicates together
// precisely because they are neighbours: keeping them in one place is what
// makes their mutual disjointness, and their disjointness from the detail
// predicate, reviewable as one fact instead of three.
//
// The partition is structural, not ordering-dependent:
//
//   * `/fleet` and `/fleet/fuel-log` are two exact literals. Neither begins
//     with either entry prefix.
//   * `isCanonicalFleetDetailPathname` accepts one segment with NO `/` in it,
//     so `/fleet/fueling/<id>` (remainder `fueling/<id>`) can never satisfy it.
//     `RESERVED_FLEET_DETAIL_SEGMENTS` additionally holds the bare `fueling`
//     and `checklist` namespace names off the record form.
//   * The two entry prefixes are distinct literals, so no pathname begins with
//     both.
//   * The bare `/fleet/fueling` and `/fleet/checklist` belong to NEITHER entry
//     surface (no trailing `/`, therefore no prefix match) and to no sibling
//     either — they are refused from the released renderer and receive
//     explicit route recovery rather than being treated as a record id.
//
// Like its sibling route contracts this module imports nothing. See
// `FLEET_ENTRY_RESERVED_SEGMENTS` for how that independence is held honest.
// ============================================================================

export const FLEET_FUELING_ENTRY_PATHNAME_PREFIX = '/fleet/fueling/';
export const FLEET_CHECKLIST_ENTRY_PATHNAME_PREFIX = '/fleet/checklist/';

/**
 * Segments directly under an entry prefix that are never an entry record id.
 *
 * It is EMPTY, and that emptiness is a finding rather than an oversight. The
 * sibling `/equipment/*` form release had to reserve `fleet` and `fuel-log`
 * because they are `ALIASES_EXACT` redirect targets, and the detail release had
 * to reserve `materials` for the same reason. The live route table carries no
 * such alias, no `VIEW_TO_PATH` row, and no nested namespace under either
 * `/fleet/fueling/` or `/fleet/checklist/`: `FLEET_ENTRY_ROUTES` declares
 * exactly `/fleet/fueling/:id` and `/fleet/checklist/:id`, and EquipmentHome
 * slices the whole remainder as one id with no sub-branch.
 *
 * The seam is kept anyway, and consulted by the predicate below, so that
 * reserving one later is a data change rather than a logic change. Because this
 * module stays import-free, the list is held honest by a static guard that
 * derives the reservable segments from `ALIASES_EXACT`, `ALIASES_PREFIX` and
 * `VIEW_TO_PATH` and fails loudly the moment either namespace gains one — the
 * same compensating control `EQUIPMENT_FUELING_FORM_RESERVED_SEGMENTS` carries,
 * and the same control that would have caught the original defect where that
 * release shipped accepting its own alias targets as machine ids.
 */
export const FLEET_ENTRY_RESERVED_SEGMENTS: readonly string[] = Object.freeze([]);

/**
 * Tokens that must never survive into a released single-segment record id,
 * checked on the RAW segment and again on the decoded one. Search and hash are
 * included deliberately: callers hand this predicate a `location.pathname`, so
 * a `?` or `#` inside it means the caller passed a whole URL, not a pathname.
 */
function hasUnsafeToken(value: string): boolean {
  return (
    value.includes('/') ||
    value.includes('\\') ||
    value.includes('\0') ||
    value.includes('\r') ||
    value.includes('\n') ||
    value.includes('?') ||
    value.includes('#')
  );
}

/**
 * Accept exactly one meaningful, decoded-safe entry-record segment under
 * `prefix`. Decoding happens ONCE and the safety checks run on both sides of
 * it, so neither an encoded separator/control character nor a dot segment can
 * widen one record capability into a nested or traversing legacy route. A
 * malformed escape throws out of `decodeURIComponent` and is refused rather
 * than retried against the raw text.
 */
function isCanonicalFleetEntryPathname(pathname: unknown, prefix: string): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(prefix)) return false;

  const rawId = pathname.slice(prefix.length);
  if (rawId === '' || hasUnsafeToken(rawId)) return false;

  let id: string;
  try {
    id = decodeURIComponent(rawId);
  } catch {
    return false;
  }

  return (
    id.trim() !== '' && id !== '.' && id !== '..' && !FLEET_ENTRY_RESERVED_SEGMENTS.includes(id) && !hasUnsafeToken(id)
  );
}

/** `/fleet/fueling/<id>` — EquipmentFuelingEntryPage, one fueling record. */
export function isCanonicalFleetFuelingEntryPathname(pathname: unknown): pathname is string {
  return isCanonicalFleetEntryPathname(pathname, FLEET_FUELING_ENTRY_PATHNAME_PREFIX);
}

/** `/fleet/checklist/<id>` — EquipmentChecklistEntryPage, one service record. */
export function isCanonicalFleetChecklistEntryPathname(pathname: unknown): pathname is string {
  return isCanonicalFleetEntryPathname(pathname, FLEET_CHECKLIST_ENTRY_PATHNAME_PREFIX);
}
